import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Modal, Form, message } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { deviceService } from '../../services/deviceService';
import { DeviceType as DeviceTypeData, DeviceTypeRequest } from '../../types';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { Search } = Input;

const DeviceType: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DeviceTypeData | null>(null);
  const [dataSource, setDataSource] = useState<DeviceTypeData[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();

  // 加载数据
  useEffect(() => {
    loadData();
  }, [pagination.current, pagination.pageSize]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await deviceService.getDeviceTypeList({
        pageNo: pagination.current,
        pageSize: pagination.pageSize,
        sortField: 'createdAt',
        sortDirection: 'desc',
      });

      if (response.data) {
        setDataSource(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error('加载设备类型失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<DeviceTypeData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '类型名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '制造商',
      dataIndex: 'firm',
      key: 'firm',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'deleted',
      key: 'deleted',
      render: (deleted: boolean) => (
        <Tag color={deleted ? 'red' : 'green'}>{deleted ? '已删除' : '正常'}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record: DeviceTypeData) => (
        <Space size="middle">
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            size="small" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            disabled={record.deleted}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    // TODO: 实现搜索功能（后端接口暂不支持）
    message.info('搜索功能待后端接口支持');
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: DeviceTypeData) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      firm: record.firm,
      description: record.description,
      apiConfig: record.apiConfig,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (record: DeviceTypeData) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除设备类型"${record.name}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deviceService.deleteDeviceType(record.id);
          // 只有当后端返回成功状态时才显示成功消息
          if (response.status === 1) {
            message.success('删除成功');
          }
          // 无论成功与否都刷新数据，因为逻辑删除可能已经生效
          loadData();
        } catch (error) {
          // 这里只处理网络错误等异常情况
          console.error('删除失败:', error);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const data: DeviceTypeRequest = {
        name: values.name,
        firm: values.firm,
        description: values.description,
        apiConfig: values.apiConfig,
      };

      if (editingRecord) {
        await deviceService.updateDeviceType(editingRecord.id, data);
        message.success('修改成功');
      } else {
        await deviceService.createDeviceType(data);
        message.success('新增成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>设备类型管理</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PlusOutlined />} type="primary" onClick={handleAdd}>
                新增类型
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索类型名称、编码"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
            </Space>
          </Col>
          {/* <Col>
            <Space>
              <Button>导出数据</Button>
            </Space>
          </Col> */}
        </Row>

        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑设备类型' : '新增设备类型'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 'active',
          }}
        >
          <Form.Item
            name="name"
            label="类型名称"
            rules={[{ required: true, message: '请输入类型名称' }]}
          >
            <Input placeholder="请输入类型名称" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="firm"
            label="制造商"
            rules={[{ required: true, message: '请输入制造商' }]}
          >
            <Input placeholder="请输入制造商（必须唯一）" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea rows={3} placeholder="请输入描述信息" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="apiConfig"
            label="API配置"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error('请输入有效的JSON格式'));
                  }
                },
              },
            ]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder='请输入JSON格式的API配置，例如：{"baseUrl":"https://api.example.com","appId":"123456"}' 
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceType;

